import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  rangePickerProps,
  rangePresets,
  useDebounce,
  formatToLocalDate,
  alias,
  formatCurrency,
  getAntdSortOrder,
  INVOICE_WARNING_VISIBLE_ROLES,
  ROLE,
  isInvoiceFeatureEnabled,
} from "../../../helpers/utils";
import { useTranslation } from "react-i18next";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { svg } from "../../../Assets/svg";
import {
  DatePicker,
  Dropdown,
  Menu,
  Select,
  Spin,
  Table,
  TableColumnsType,
  message,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import InfiniteScroll from "react-infinite-scroll-component";
import NoDataMessage from "../../Components/NoData";
import FullScreenLoader from "../../Components/FullScreenLoader";
import { useLocation, useNavigate } from "react-router-dom";
import {
  deleteQuotationAPI,
  duplicateQuotationAPI,
  createRevisionQuotationAPI,
  fetchQuotationsQueryFn,
  generateQuotationPdfAPI,
  getQuotationFiltersAPI,
  notifyClientAPI,
  reopenQuotationAPI,
  updateQuotatioStatusAPI,
  toggleArchiveQuoteAPI,
  previewQuoteEmailAPI,
  openSystemMailer,
} from "./quotationApis";
import {
  CreateQuotationStepsEnum,
  QUOTATION_FILTER_KEY,
  QUOTATION_REFETCH_KEY,
  QUOTATION_SCROLL_KEY,
  QuoteStatus,
  QuoteStatusLabels,
  getQuoteStatusOptions,
  WarningSeverity,
} from "./quotationTypes";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";
import { DownloadMailerAppModal } from "../../../Components/DownloadMailerAppModal";
import { useSocket } from "../../../contexts/SocketContext";
import axiosInstance from "../../../helpers/axios";
import {
  decryptByText,
  getDecryptedName,
  getDecryptedUserData,
  isCustomerLoggedIn,
  isAdminSubAdminLoggedIn,
  getUserRole,
} from "../../../helpers/bycrypt";
import { RoleTypesEnum } from "../RolesAndPermissions/rolesAndPermissionsTypes";
import AddApprovalOverrideNoteModal from "./Components/AddApprovalOverrideNoteModal";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import CreateOrderConfirmationModal from "./Components/CreateOrderConfirmationModal";

import { createOrderAPI } from "../Orders/orderApis";
import { resetQuotationState } from "./quotationSlice";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import ClearComponent from "../../Components/ClearComponent";
import {
  WarningsModal,
  WarningActionLayouts,
} from "../../../Components/WarningsModal";
import { ModifyDeliveryAddressModal } from "../ClientsDistributors/Components/ModifyDeliveryAddressModal";
import ConfigureDrawingsModal from "./Components/ConfigureDrawingsModal";
import ViewProfitDataModal from "../Orders/Components/Modals/ViewProfitDataModal";
import { Capacitor } from "@capacitor/core";
import { getCapacitorBaseUrl } from "../../../helpers/common";
import { openQuoteEmailWithPdf } from "../../../helpers/emailCapacitor";
import {
  DesktopUpdateModal,
  isVersionNewer,
} from "../../../Components/DesktopUpdateModal";
import CustomerOnHoldWarningModal from "../Orders/Components/Modals/CustomerOnHoldWarningModal";
import QuotationSyncReviewModal, {
  SyncWarningItem,
  SyncUpdateItem,
} from "../../../Components/QuotationSyncReviewModal";

dayjs.extend(utc);
const { RangePicker }: any = DatePicker;

const LIMIT = 25;

// Intercept browser reloads and clear filter persistence so the page starts completely fresh
try {
  const navEntries = performance.getEntriesByType("navigation");
  const isReload =
    navEntries.length > 0 &&
    (navEntries[0] as PerformanceNavigationTiming).type === "reload";
  const isLegacyReload =
    performance.navigation && performance.navigation.type === 1;

  if (isReload || isLegacyReload) {
    sessionStorage.removeItem(QUOTATION_FILTER_KEY);
    sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
  }
} catch (e) {}

// Helper: read persisted filters from sessionStorage
const getPersistedFilters = () => {
  try {
    const raw = sessionStorage.getItem(QUOTATION_FILTER_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
};

// Helper: write current filters to sessionStorage
const persistFilters = (filters: Record<string, any>) => {
  sessionStorage.setItem(QUOTATION_FILTER_KEY, JSON.stringify(filters));
};

// --- Helper: Status Class Mapping ---
export const statusFindClass = (status: string) => {
  switch (status) {
    case QuoteStatus.DRAFT:
      return "draftStatus";

    case QuoteStatus.SAVED:
      return "savedStatus";

    case QuoteStatus.SENT:
      return "sentStatus";

    case QuoteStatus.DESIGN_PENDING:
      return "designPendingStatus";

    case QuoteStatus.DESIGN_APPROVED:
      return "designApprovedStatus";

    case QuoteStatus.APPROVED:
    case QuoteStatus.APPROVED_OVERRIDE:
      return "approvedStatus";

    case QuoteStatus.EXPIRED:
      // case QuoteStatus.REJECTED:
      return "expiredStatus";

    case QuoteStatus.ORDER_CREATED:
      return "orderCreatedStatus";
    case QuoteStatus.PENDING_ADMIN_APPROVAL:
      return "pendingAdminApproval";
    case QuoteStatus.ADMIN_APPROVED:
      return "adminApproved";

    default:
      return "draftStatus"; // Default fallback
  }
};

const Quotations = () => {
  const location = useLocation();
  const { quote_status, hideFilterUi } = location.state || {};
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.QUOTATIONS,
  );
  const { authLoading } = useAppSelector((state: RootState) => state.loading);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const user = getDecryptedUserData();
  const userId = user?._id || "";
  const { socket, isConnected }: any = useSocket();

  const [failedAddressData, setFailedAddressData] = useState<any>(null);

  const [drawingRequiredData, setDrawingRequiredData] = useState<any>(null);
  const pendingOrderPayloadRef = useRef<any>(null);

  const [isMounting, setIsMounting] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsMounting(false), 20);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (location?.state?.quote_status) {
      navigate(location.pathname, { replace: true });
    }
  }, [location?.state?.quote_status, navigate, location.pathname]);

  // ─── Filters (restored from sessionStorage on mount) ───────────────────────
  const _pf = getPersistedFilters();

  const [search, setSearch] = useState<string>(
    quote_status ? "" : _pf?.search ?? "",
  );
  const debouncedSearch = useDebounce(search, 500);
  const [startDate, setStartDate] = useState<any>(
    quote_status ? "" : _pf?.startDate ?? "",
  );
  const [endDate, setEndDate] = useState<any>(
    quote_status ? "" : _pf?.endDate ?? "",
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    quote_status || _pf?.statusFilter || "",
  );
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(
    quote_status ? false : _pf?.isFilterOpen ?? false,
  );
  const [companyFilter, setCompanyFilter] = useState<string>(
    quote_status ? "" : _pf?.companyFilter ?? "",
  );
  const [companySearch, setCompanySearch] = useState<string>("");
  const [showDesktopUpdateModal, setShowDesktopUpdateModal] =
    useState<boolean>(false);
  const [desktopAppCurrentVersion, setDesktopAppCurrentVersion] =
    useState<string>("1.0.1");
  const pendingSendMailRef = useRef<(() => void) | null>(null);
  const [createdByFilter, setCreatedByFilter] = useState<string>(
    quote_status ? "" : _pf?.createdByFilter ?? "",
  );
  const [createdBySearch, setCreatedBySearch] = useState<string>("");
  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>(
    quote_status
      ? { field: null, order: null }
      : _pf?.sorterState ?? { field: null, order: null },
  );
  const isArchiveView = location.pathname.includes("/archived");
  const [archiveFilter, setArchiveFilter] = useState<boolean>(
    isArchiveView || (_pf?.archiveFilter ?? false),
  );
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);

  const [companyOptions, setCompanyOptions] = useState<
    Array<{ label: string; value: string; companyName: string; email: string }>
  >([]);
  const [createdByOptions, setCreatedByOptions] = useState<
    Array<{ label: string; value: string; role: string }>
  >([]);

  // --- Modal states ---
  const [showDesktopAppError, setShowDesktopAppError] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [idToDuplicate, setIdToDuplicate] = useState<string | null>(null);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);

  const [idToCreateRevision, setIdToCreateRevision] = useState<string | null>(
    null,
  );
  const [isCreatingRevision, setIsCreatingRevision] = useState<boolean>(false);
  const [syncReviewModalData, setSyncReviewModalData] = useState<{
    actionType: "duplicate" | "revision";
    quoteId: string;
    warnings: SyncWarningItem[];
    updates: SyncUpdateItem[];
  } | null>(null);
  const [isConfirmingSync, setIsConfirmingSync] = useState<boolean>(false);
  const [archiveActionData, setArchiveActionData] = useState<{
    id: string;
    type: "archive" | "unarchive";
  } | null>(null);
  const [idToOverride, setIdToOverride] = useState<string | null>(null);
  const [idToNotify, setIdToNotify] = useState<string | null>(null);
  const [isNotifying, setIsNotifying] = useState(false);
  const [apiWarnings, setApiWarnings] = useState<any[]>([]);
  const [apiWarningSeverity, setApiWarningSeverity] = useState<
    WarningSeverity | ""
  >("");
  const [pendingNotifyId, setPendingNotifyId] = useState<string | null>(null);
  const [pendingStatusData, setPendingStatusData] = useState<{
    status:
      | "saved"
      | "sent"
      | "design_pending"
      | "design_approved"
      | "admin_approved";
    quotationId: string;
  } | null>(null);
  const [idToAdminApprove, setIdToAdminApprove] = useState<string | null>(null);
  const [isAdminApproving, setIsAdminApproving] = useState<boolean>(false);
  const [idToDownloadAndMarkSent, setIdToDownloadAndMarkSent] = useState<
    string | null
  >(null);
  const [isDownloadingAndMarkingSent, setIsDownloadingAndMarkingSent] =
    useState<boolean>(false);
  const [pendingApprovalCustomerId, setPendingApprovalCustomerId] = useState<
    string | null
  >(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [pendingSentQuoteId, setPendingSentQuoteId] = useState<string | null>(
    null,
  );
  const [pendingSentForceUpdate, setPendingSentForceUpdate] =
    useState<boolean>(false);
  const [isContantLoading, setIsContantLoading] = useState<boolean>(true);
  const mailerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [exporting, setExporting] = useState(false);
  const [idToReopen, setIdToReopen] = useState<string | null>(null);
  const [isReopening, setIsReopening] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handleMailerLoader = (data: any) => {
      console.log("📥 Received mailer:trigger-loader event:", data);
      if (mailerTimeoutRef.current) {
        clearTimeout(mailerTimeoutRef.current);
        mailerTimeoutRef.current = null;
      }
      if (
        data &&
        (data.loader === false ||
          data.loading === false ||
          data.loder === false ||
          data.status === "success" ||
          data.status === "error")
      ) {
        setIsContantLoading(false);
      } else if (
        data &&
        (data.loader === true ||
          data.loading === true ||
          data.loder === true ||
          data.status === "processing")
      ) {
        setIsContantLoading(true);
      }
    };

    socket.on("mailer:trigger-loader", handleMailerLoader);

    return () => {
      socket.off("mailer:trigger-loader", handleMailerLoader);
      if (mailerTimeoutRef.current) {
        clearTimeout(mailerTimeoutRef.current);
      }
    };
  }, [socket]);

  const triggerEmailWithDesktopAppCheck = (sendEmailCallback: () => void) => {
    if (!socket || !isConnected) {
      console.error("Global socket is not connected!");
      message.destroy("opening_mailer_key");
      setIsChangingStatus(false);
      setShowDesktopAppError(true);
      return;
    }

    const targetRoom = localStorage.getItem("sm_target_room");
    const requiredVersion =
      process.env.REACT_APP_DESKTOP_APP_VERSION || "1.0.1";

    let responseReceived = false;
    let checkVersionTimeout: NodeJS.Timeout | null = null;

    const handleCheckVersionResponse = (data: any) => {
      if (responseReceived) return;
      responseReceived = true;

      if (checkVersionTimeout) {
        clearTimeout(checkVersionTimeout);
        checkVersionTimeout = null;
      }
      if (socket) {
        socket.off("check_version_response", handleCheckVersionResponse);
      }

      console.log("📥 Received check_version_response payload:", data);
      const currentVersion =
        data?.current_version || data?.app_version || data?.version || "1.0.0";
      setDesktopAppCurrentVersion(currentVersion);

      const isOutdated = isVersionNewer(requiredVersion, currentVersion);
      const isMatched =
        !isOutdated &&
        (data?.is_matched !== undefined ? Boolean(data.is_matched) : true);

      if (!isMatched) {
        console.warn(
          `⚠️ Desktop app version mismatch. Required: ${requiredVersion}, Installed: ${currentVersion}`,
        );
        message.destroy("opening_mailer_key");
        setIsChangingStatus(false);
        pendingSendMailRef.current = () => {
          sendEmailCallback();
        };
        setShowDesktopUpdateModal(true);
      } else {
        console.log("✅ Desktop app is running and version matches!");
        sendEmailCallback();
      }
    };

    checkVersionTimeout = setTimeout(() => {
      if (!responseReceived) {
        responseReceived = true;
        if (socket) {
          socket.off("check_version_response", handleCheckVersionResponse);
        }
        console.warn(
          "⚠️ Desktop Mailer App did not respond to check_version within timeout.",
        );
        message.destroy("opening_mailer_key");
        setIsChangingStatus(false);

        if (targetRoom) {
          console.warn(
            "⚠️ Legacy desktop app detected. Prompting update modal.",
          );
          setDesktopAppCurrentVersion("1.0.0 (Legacy)");
          pendingSendMailRef.current = () => {
            sendEmailCallback();
          };
          setShowDesktopUpdateModal(true);
        } else {
          setShowDesktopAppError(true);
        }
      }
    }, 2500);

    socket.on("check_version_response", handleCheckVersionResponse);

    console.log(
      "📤 Emitting check_version to socket for target room:",
      targetRoom || "all",
    );
    socket.emit("check_version", {
      target_room: targetRoom || undefined,
      required_version: requiredVersion,
      version: requiredVersion,
    });
  };

  const [submittingCreateOrder, setSubmittingCreateOrder] = useState(false);
  const [selectedQuoteData, setSelectedQuoteData] = useState<any>(null);
  const [isCreateOrder, setIsCreateOrder] = useState(false);
  const [customerOnHoldModal, setCustomerOnHoldModal] = useState(false);
  const [customerOnHoldMessage, setCustomerOnHoldMessage] = useState("");
  const messageCloserRef = useRef<any>(null);
  const [profitData, setProfitData] = useState<any>(null);
  const [profitModal, setProfitModal] = useState(false);
  // Cleanup message on unmount
  useEffect(() => {
    return () => {
      if (messageCloserRef.current) {
        messageCloserRef.current();
      }
    };
  }, []);

  // ─── Scroll restoration refs ────────────────────────────────────────────────
  const scrollableDivRef = useRef<HTMLDivElement | null>(null);
  const scrollRestoredRef = useRef(false);
  const scrollableDivId = useMemo(
    () => `quotationsScrollDiv-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  const getFilterCount = () => {
    let count = 0;
    if (statusFilter) count++;
    if (companyFilter) count++;
    if (createdByFilter) count++;
    if (startDate && endDate) count++;
    if (archiveFilter) count++;
    return count;
  };

  // ─── TanStack Query filters object ─────────────────────────────────────────
  const queryFilters = {
    search: debouncedSearch,
    date_from: startDate,
    date_to: endDate,
    sortBy: sorterState.field,
    sortOrder: sorterState.order,
    status: statusFilter,
    customer_id: [companyFilter as string],
    created_by: [createdByFilter as string],
    is_archived: archiveFilter.toString(),
  };

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["quotations", queryFilters],
    queryFn: ({ pageParam = 1, signal }) =>
      fetchQuotationsQueryFn(
        {
          page: pageParam as number,
          limit: LIMIT,
          ...queryFilters,
        },
        signal,
      ),
    getNextPageParam: (lastPage: any, allPages) => {
      const fetched = allPages.reduce(
        (acc: number, p: any) => acc + (p?.quotes?.length || 0),
        0,
      );
      const total = lastPage?.total || 0;
      return fetched < total ? allPages.length + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    initialPageParam: 1,
  });

  const quotationsList: any[] = queryData
    ? queryData.pages.flatMap((p: any) => p?.quotes || [])
    : [];
  const totalCount: number = queryData?.pages?.[0]?.total || 0;
  const isAdminApprovalOn: boolean =
    queryData?.pages?.[0]?.admin_approval_required ?? false;
  console.log("isAdminApprovalOn", isAdminApprovalOn);

  // ─── Scroll restoration after back-navigation ───────────────────────────────
  useLayoutEffect(() => {
    if (
      !scrollRestoredRef.current &&
      quotationsList.length > 0 &&
      !isLoading &&
      !isMounting
    ) {
      const savedScroll = sessionStorage.getItem(QUOTATION_SCROLL_KEY);
      if (savedScroll) {
        const parent = scrollableDivRef.current;
        const target =
          parent?.querySelector(".infinite-scroll-component") || parent;
        // const target = scrollableDivRef.current;
        if (target) {
          target.scrollTop = parseInt(savedScroll, 10);
          requestAnimationFrame(() => {
            setTimeout(() => {
              target.dispatchEvent(new Event("scroll", { bubbles: true }));
            }, 100);
          });
        }
        sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
      }
      scrollRestoredRef.current = true;
    }
  }, [quotationsList, isLoading]);

  // ─── Reset scroll flag & scroll to top when filters change ─────────────────
  const isFirstMountForFilters = useRef(true);
  useEffect(() => {
    if (isFirstMountForFilters.current) {
      isFirstMountForFilters.current = false;
      return;
    }
    scrollRestoredRef.current = false;
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0;
    }
    queryClient.removeQueries({ queryKey: ["quotations"], type: "inactive" });
  }, [
    debouncedSearch,
    startDate,
    endDate,
    sorterState,
    statusFilter,
    companyFilter,
    createdByFilter,
  ]);

  // ─── Persist filters to sessionStorage on every change ─────────────────────
  useEffect(() => {
    persistFilters({
      search,
      startDate,
      endDate,
      sorterState,
      isFilterOpen,
      statusFilter,
      companyFilter,
      createdByFilter,
      isArchiveView,
      archiveFilter,
    });
  }, [
    search,
    startDate,
    endDate,
    sorterState,
    isFilterOpen,
    statusFilter,
    companyFilter,
    createdByFilter,
    isArchiveView,
    archiveFilter,
  ]);

  // ─── On mount: if returning from create/edit, refetch ──────────────────────
  useEffect(() => {
    if (sessionStorage.getItem(QUOTATION_REFETCH_KEY) === "1") {
      sessionStorage.removeItem(QUOTATION_REFETCH_KEY);
      queryClient.removeQueries({ queryKey: ["quotations"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load filter dropdown options ───────────────────────────────────────────
  useEffect(() => {
    dispatch(
      getQuotationFiltersAPI({
        onSuccess: (data) => {
          const companies = data?.customers || [];
          const uniqueCompaniesMap = new Map();
          companies.forEach((comp: any) => {
            if (comp?._id && !uniqueCompaniesMap.has(String(comp._id))) {
              uniqueCompaniesMap.set(String(comp._id), {
                label: getDecryptedName(comp) || "Unknown",
                value: comp._id,
                companyName: comp.company_name || "",
                email: decryptByText(comp.email) || "",
              });
            }
          });
          setCompanyOptions(Array.from(uniqueCompaniesMap.values()));

          const users = data?.created_by || [];
          const uniqueUsersMap = new Map();
          users.forEach((user: any) => {
            if (user?._id && !uniqueUsersMap.has(String(user._id))) {
              uniqueUsersMap.set(String(user._id), {
                label: getDecryptedName(user) || "Unknown",
                value: user._id,
                role: user.role?.name || "",
              });
            }
          });
          setCreatedByOptions(Array.from(uniqueUsersMap.values()));
        },
        onError: () => {},
      }),
    );
  }, []);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter.order) {
      const order = sorter.order === "ascend" ? "asc" : "desc";
      setSorterState({ field: sorter.columnKey, order });
    } else {
      setSorterState({ field: null, order: null });
    }
  };

  const searchBarHandler = (e: any) => {
    setSearch(e.target.value);
  };

  const searchBarClean = () => {
    setSearch("");
  };

  const onRangeChange = (dates: any) => {
    if (dates) {
      setStartDate(dates[0]?.format("YYYY-MM-DD"));
      setEndDate(dates[1]?.format("YYYY-MM-DD"));
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const toggleFilter = () => {
    setIsFilterOpen((prevState) => !prevState);
  };

  const onCellClick = (record: any) => ({
    onClick: () => {
      if (USER_PERMISSIONS.READ) {
        const parent = scrollableDivRef?.current;
        const target =
          parent?.querySelector(".infinite-scroll-component") || parent;
        if (target) {
          sessionStorage.setItem(
            QUOTATION_SCROLL_KEY,
            String(target.scrollTop),
          );
        }
        navigate(`/quotations/${record._id}?tab=customer-details`);
      }
    },
    style: { cursor: USER_PERMISSIONS.READ ? "pointer" : "default" },
  });

  const handleDeleteConfirm = () => {
    if (!idToDelete) return;
    setDeleting(true);
    dispatch(
      deleteQuotationAPI({
        id: idToDelete,
        onSuccess: () => {
          setDeleting(false);
          setIdToDelete(null);
          // Remove from cache optimistically
          queryClient.setQueryData(["quotations", queryFilters], (old: any) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                quotes: (page.quotes || []).filter(
                  (q: any) => q._id !== idToDelete,
                ),
                total: Math.max(0, (page.total || 1) - 1),
              })),
            };
          });
        },
        onError: () => setDeleting(false),
      }),
    );
  };

  const handleArchiveConfirm = () => {
    if (!archiveActionData) return;
    setIsTogglingArchive(true);
    dispatch(
      toggleArchiveQuoteAPI({
        quote_id: archiveActionData.id,
        is_archived: archiveActionData.type === "archive",
        onSuccess: () => {
          sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
          sessionStorage.removeItem(QUOTATION_FILTER_KEY);
          sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
          setIsTogglingArchive(false);
          setArchiveActionData(null);
          queryClient.removeQueries({ queryKey: ["quotations"] });
        },
        onError: () => {
          setIsTogglingArchive(false);
        },
      }),
    );
  };

  const executeStatusUpdate = (
    status:
      | "saved"
      | "sent"
      | "design_pending"
      | "design_approved"
      | "admin_approved",
    quotationId: string,
    forceUpdate: boolean = false,
    options?: {
      onSuccess?: () => void;
      onError?: () => void;
      onFinally?: () => void;
    },
  ) => {
    setIsChangingStatus(true);
    const payload = {
      quote_id: quotationId as string,
      status,
      force_update: forceUpdate,
    };
    dispatch(
      updateQuotatioStatusAPI({
        payload,
        onSuccess: () => {
          setIsChangingStatus(false);
          setPendingStatusData(null);
          setApiWarnings([]);
          queryClient.setQueryData(["quotations", queryFilters], (old: any) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                quotes: (page.quotes || []).map((q: any) =>
                  q._id === quotationId ? { ...q, status } : q,
                ),
              })),
            };
          });
          options?.onSuccess?.();
          options?.onFinally?.();
        },
        onWarnings: (warnings, severity) => {
          setIsChangingStatus(false);
          setApiWarnings(warnings);
          setApiWarningSeverity(severity || "");
          setPendingStatusData({ status, quotationId });
          options?.onFinally?.();
        },
        onError: () => {
          setIsChangingStatus(false);
          options?.onError?.();
          options?.onFinally?.();
        },
        onNoItemsError: () => {
          setIsChangingStatus(false);
          options?.onError?.();
          options?.onFinally?.();
        },
      }),
    );
  };

  const changeStatusHandler = (
    status:
      | "saved"
      | "sent"
      | "design_pending"
      | "design_approved"
      | "admin_approved",
    quotationId: string,
    forceUpdate: boolean = false,
  ) => {
    if (status === "sent") {
      setIsChangingStatus(true);
      message.loading({
        content: "Preparing email format and opening mail app...",
        key: "opening_mailer_key",
        duration: 0,
      });
      dispatch(
        previewQuoteEmailAPI({
          payload: { quote_id: quotationId },
          onSuccess: async (emailData) => {
            setIsChangingStatus(false);
            message.destroy("opening_mailer_key");
            // openSystemMailer(emailData);
            try {
              if (Capacitor.isNativePlatform()) {
                message.loading({
                  content: "Preparing quotation PDF and opening mail app...",
                  key: "opening_mailer_key",
                  duration: 0,
                });
                try {
                  await openQuoteEmailWithPdf(quotationId, emailData);
                  message.destroy("opening_mailer_key");
                  setIsContantLoading(false);
                  setPendingSentQuoteId(quotationId);
                  setPendingSentForceUpdate(forceUpdate);
                } catch (error) {
                  console.error("Native email composer error:", error);
                  message.destroy("opening_mailer_key");
                  message.error(
                    "Failed to prepare the quotation email. Please try again.",
                  );
                }
                return;
              }
              triggerEmailWithDesktopAppCheck(() => {
                const targetRoom = localStorage.getItem("sm_target_room");
                socket.emit("mailer:trigger-email", {
                  target_room: targetRoom || undefined,
                  to: emailData.to || "",
                  cc: emailData.cc || "",
                  subject: emailData.mail_subject || "",
                  body: emailData.html_template || "",
                  pdf_url: emailData.pdf_url || "",
                  pdf_download_url: `${process.env.REACT_APP_API_URL}/quotes/generate-pdf?quote_id=${quotationId}&download=true`,
                  pdf_name: emailData.pdf_name || "Quotation.pdf",
                });

                setIsContantLoading(true);
                setPendingSentQuoteId(quotationId);
                setPendingSentForceUpdate(forceUpdate);

                if (mailerTimeoutRef.current)
                  clearTimeout(mailerTimeoutRef.current);
                mailerTimeoutRef.current = setTimeout(() => {
                  setIsContantLoading(false);
                  setPendingSentQuoteId(null);
                  setShowDesktopAppError(true);
                }, 4000);
              });
            } catch (error) {
              console.error(
                "Could not connect to Desktop App. Is it running?",
                error,
              );
              message.destroy("opening_mailer_key");
              setShowDesktopAppError(true);
            }
          },
          onError: () => {
            setIsChangingStatus(false);
            message.destroy("opening_mailer_key");
          },
        }),
      );
      return;
    }

    executeStatusUpdate(status, quotationId, forceUpdate);
  };

  const MoreOptionsMenu = (data: any) => {
    const isCreatedForSelf = userId === data?.customer_id?._id;
    const isClientViewingOwnQuote = isCustomerLoggedIn() && isCreatedForSelf;
    const isClientViewingOtherUserQuote =
      isCustomerLoggedIn() && !isCreatedForSelf;

    if (isClientViewingOwnQuote) {
      return (
        <Menu>
          {data?.status === QuoteStatus.DRAFT && (
            <>
              {USER_PERMISSIONS.MODIFY && (
                <Menu.Item
                  key="1"
                  onClick={() => {
                    sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
                    sessionStorage.removeItem(QUOTATION_FILTER_KEY);
                    sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
                    navigate(
                      `/quotations/create-quotation?step=${CreateQuotationStepsEnum.CUSTOMER_DETAILS}&quotationId=${data._id}&mode=edit`,
                    );
                  }}
                >
                  <div className="text-black d-flex align-items-center gap-2 svg-20">
                    Modify
                  </div>
                </Menu.Item>
              )}
              {USER_PERMISSIONS.REMOVE && (
                <Menu.Item key="3" onClick={() => setIdToDelete(data._id)}>
                  <div className="text-danger d-flex align-items-center gap-2 svg-20">
                    Remove
                  </div>
                </Menu.Item>
              )}
            </>
          )}
          {USER_PERMISSIONS.READ &&
            data?.status !== QuoteStatus.DRAFT &&
            data?.status !== QuoteStatus.EXPIRED && (
              <Menu.Item
                key="quotation_pdf"
                onClick={() => {
                  if (exporting) return;
                  const closeMessage = message.loading(
                    "Generating PDF, please wait...",
                    0,
                  );
                  messageCloserRef.current = closeMessage;
                  setExporting(true);
                  dispatch(
                    generateQuotationPdfAPI({
                      payload: { quote_id: data._id },
                      onSuccess: () => {
                        closeMessage();
                        setExporting(false);
                        messageCloserRef.current = null;
                      },
                      onError: () => {
                        closeMessage();
                        setExporting(false);
                        messageCloserRef.current = null;
                      },
                    }),
                  );
                }}
              >
                Quotation PDF
              </Menu.Item>
            )}
        </Menu>
      );
    }

    if (isClientViewingOtherUserQuote) {
      return (
        <Menu>
          {USER_PERMISSIONS.READ && data?.status !== QuoteStatus.EXPIRED && (
            <Menu.Item
              key="quotation_pdf"
              onClick={() => {
                if (exporting) return;
                const closeMessage = message.loading(
                  "Generating PDF, please wait...",
                  0,
                );
                messageCloserRef.current = closeMessage;
                setExporting(true);
                dispatch(
                  generateQuotationPdfAPI({
                    payload: { quote_id: data._id },
                    onSuccess: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                    onError: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                  }),
                );
              }}
            >
              Quotation PDF
            </Menu.Item>
          )}
        </Menu>
      );
    }

    return (
      <Menu>
        {/* ✅ Add/Modify Guard (Create Order) */}
        {USER_PERMISSIONS.ADD &&
          [
            QuoteStatus.SAVED,
            QuoteStatus.SENT,
            QuoteStatus.ADMIN_APPROVED,
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
          ].includes(data?.status) && (
            <Menu.Item
              key="create-order"
              onClick={() => {
                setSelectedQuoteData(data);
                setIsCreateOrder(true);
              }}
            >
              Create Order
            </Menu.Item>
          )}
        {/* {USER_PERMISSIONS.MODIFY &&
          [QuoteStatus.EXPIRED].includes(data?.status) && (
            <Menu.Item
              key="re-open"
              onClick={() => {
                setIdToReopen(data._id);
              }}
            >
              Re-Open
            </Menu.Item>
          )} */}
        {USER_PERMISSIONS.MODIFY &&
          (isAdminSubAdminLoggedIn() || !isAdminApprovalOn
            ? data?.is_design === false ||
              ![QuoteStatus.DRAFT, QuoteStatus.DESIGN_PENDING].includes(
                data?.status,
              )
            : (data?.is_design === false ||
                ![QuoteStatus.DRAFT, QuoteStatus.DESIGN_PENDING].includes(
                  data?.status,
                )) &&
              ![
                QuoteStatus.DRAFT,
                QuoteStatus.DESIGN_APPROVED,
                QuoteStatus.PENDING_ADMIN_APPROVAL,
              ].includes(data?.status)) &&
          data?.status !== QuoteStatus.ORDER_CREATED &&
          ![
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
            QuoteStatus.EXPIRED,
          ].includes(data?.status) && (
            <>
              {/* <Menu.Item
                key="save"
                onClick={() => {
                  changeStatusHandler("saved", data._id);
                }}
              >
                Save
              </Menu.Item> */}
              <Menu.Item
                key="save&send"
                onClick={() => {
                  changeStatusHandler("sent", data._id);
                }}
              >
                Email & Mark Sent
              </Menu.Item>
            </>
          )}

        {/* {[QuoteStatus.DESIGN_PENDING].includes(data?.status) &&
          [
            RoleTypesEnum.ADMIN,
            RoleTypesEnum.SUB_ADMIN,
            RoleTypesEnum.DESIGN_TEAM,
          ].includes(getUserRole() as RoleTypesEnum) &&
          data?.is_design && (
            <Menu.Item
              key="mark_design_approved"
              onClick={() => {
                changeStatusHandler("design_approved", data._id);
              }}
            >
              Mark Design Approved
            </Menu.Item>
          )} */}

        {!isCustomerLoggedIn() &&
          isAdminSubAdminLoggedIn() &&
          isAdminApprovalOn &&
          [QuoteStatus.DRAFT, QuoteStatus.PENDING_ADMIN_APPROVAL].includes(
            data?.status,
          ) && (
            <Menu.Item
              key="mark_admin_approved"
              onClick={() => {
                if (data?.customer_snapshot?.is_approved === false) {
                  const custId =
                    typeof data?.customer_id === "object"
                      ? data?.customer_id?._id
                      : data?.customer_id;
                  setPendingApprovalCustomerId(custId || null);
                } else {
                  setIdToAdminApprove(data._id);
                }
              }}
            >
              Mark Admin Approved
            </Menu.Item>
          )}

        {/* {USER_PERMISSIONS.READ &&
          [QuoteStatus.SAVED, QuoteStatus.SENT].includes(data?.status) && (
            <Menu.Item
              key="share_approval_link"
              onClick={async () => {
                // 1. Get the base (http://localhost:3000/sm-crm)
                // window.location.origin handles the protocol and port automatically
                const baseUrl = alias
                  ? `${window.location.origin}/${alias}`
                  : window.location.origin;

                // 2. Get the dynamic ID (assuming 'id' is the property in your data object)
                const id = data._id;
                if (!id) {
                  console.error("No quotation ID found in data.");
                  return;
                }

                // 3. Construct the full URL
                let fullUrl = `${baseUrl}/quotation-details/${id}`;

                if (Capacitor.isNativePlatform()) {
                  fullUrl = getCapacitorBaseUrl(fullUrl);
                }

                try {
                  // 4. Copy to clipboard
                  await navigator.clipboard.writeText(fullUrl);
                  message.success("Approval link copied to clipboard!");
                  // Optional: Add your UI notification here (e.g., message.success)
                  console.log("Copied:", fullUrl);
                } catch (err) {
                  console.error("Failed to copy URL:", err);
                }
              }}
            >
              Share Approval Link
            </Menu.Item>
          )} */}

        {/* ✅ Modify Guard (Approval Override) */}
        {/* {!isCustomerLoggedIn() &&
          USER_PERMISSIONS.MODIFY &&
          [QuoteStatus.SAVED, QuoteStatus.SENT].includes(data?.status) && (
            <Menu.Item
              key="approval_override"
              onClick={() => setIdToOverride(data._id)}
              style={{ color: "#52c41a" }}
            >
              Approval Override
            </Menu.Item>
          )} */}

        {/* ✅ Modify Guard (Notify Client) */}
        {!isCustomerLoggedIn() &&
          USER_PERMISSIONS.MODIFY &&
          [QuoteStatus.SAVED, QuoteStatus.SENT].includes(data?.status) && (
            <Menu.Item
              key="notify_cleint"
              onClick={() => {
                setIsChangingStatus(true);
                message.loading({
                  content: "Preparing email format and opening mail app...",
                  key: "opening_mailer_key",
                  duration: 0,
                });
                dispatch(
                  previewQuoteEmailAPI({
                    payload: { quote_id: data._id },
                    onSuccess: async (emailData) => {
                      setIsChangingStatus(false);
                      message.destroy("opening_mailer_key");
                      try {
                        if (Capacitor.isNativePlatform()) {
                          message.loading({
                            content:
                              "Preparing quotation PDF and opening mail app...",
                            key: "opening_mailer_key",
                            duration: 0,
                          });
                          try {
                            await openQuoteEmailWithPdf(data._id, emailData);
                            message.destroy("opening_mailer_key");
                            if (data?.status === QuoteStatus.SAVED) {
                              setIsContantLoading(false);
                              setPendingSentQuoteId(data._id);
                              setPendingSentForceUpdate(false);
                            }
                          } catch (error) {
                            console.error(
                              "Native email composer error:",
                              error,
                            );
                            message.destroy("opening_mailer_key");
                            message.error(
                              "Failed to prepare the quotation email. Please try again.",
                            );
                          }
                          return;
                        }
                        triggerEmailWithDesktopAppCheck(() => {
                          const targetRoom =
                            localStorage.getItem("sm_target_room");
                          socket.emit("mailer:trigger-email", {
                            target_room: targetRoom || undefined,
                            to: emailData.to || "",
                            cc: emailData.cc || "",
                            subject: emailData.mail_subject || "",
                            body: emailData.html_template || "",
                            pdf_url: emailData.pdf_url || "",
                            pdf_download_url: `${process.env.REACT_APP_API_URL}/quotes/generate-pdf?quote_id=${data._id}&download=true`,
                            pdf_name: emailData.pdf_name || "Quotation.pdf",
                          });

                          console.log("Email triggered to Backend!");
                          if (data?.status === QuoteStatus.SAVED) {
                            setPendingSentQuoteId(data._id);
                            setPendingSentForceUpdate(false);
                          }
                        });
                      } catch (error) {
                        console.error(
                          "Could not connect to Desktop App. Is it running?",
                          error,
                        );
                        message.destroy("opening_mailer_key");
                        setShowDesktopAppError(true);
                      }
                    },
                    onError: () => {
                      setIsChangingStatus(false);
                      message.destroy("opening_mailer_key");
                    },
                  }),
                );
              }}
            >
              Notify to Client
            </Menu.Item>
          )}

        {/* ✅ Read Guard (PDF Generation/Export) */}
        {!archiveFilter &&
          USER_PERMISSIONS.READ &&
          [
            QuoteStatus.SAVED,
            QuoteStatus.SENT,
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
            QuoteStatus.ORDER_CREATED,
          ].includes(data?.status) && (
            <Menu.Item
              key="quotation_pdf"
              onClick={() => {
                if (exporting) return;
                const closeMessage = message.loading(
                  "Generating PDF, please wait...",
                  0,
                );
                messageCloserRef.current = closeMessage;
                setExporting(true);
                dispatch(
                  generateQuotationPdfAPI({
                    payload: { quote_id: data._id },
                    onSuccess: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                    onError: () => {
                      closeMessage();
                      setExporting(false);
                      messageCloserRef.current = null;
                    },
                  }),
                );
              }}
            >
              Quotation PDF
            </Menu.Item>
          )}

        {/* Download & Mark Sent (Admin Approved only) */}
        {!archiveFilter &&
          USER_PERMISSIONS.MODIFY &&
          data?.status === QuoteStatus.ADMIN_APPROVED && (
            <Menu.Item
              key="download_and_mark_sent"
              onClick={() => {
                setIdToDownloadAndMarkSent(data._id);
              }}
            >
              Download & Mark Sent
            </Menu.Item>
          )}

        {/* ✅ Modify Guard (Edit Quotation) */}
        {!archiveFilter &&
          USER_PERMISSIONS.MODIFY &&
          ![
            QuoteStatus.APPROVED,
            QuoteStatus.APPROVED_OVERRIDE,
            QuoteStatus.EXPIRED,
            QuoteStatus.ORDER_CREATED,
            QuoteStatus.SENT,
          ].includes(data.status) &&
          (data?.status != QuoteStatus?.ADMIN_APPROVED ||
            [ROLE?.ADMIN, ROLE?.SUB_ADMIN]?.includes(user?.role?.key)) && (
            <Menu.Item
              key="1"
              onClick={() => {
                sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
                sessionStorage.removeItem(QUOTATION_FILTER_KEY);
                sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
                navigate(
                  `/quotations/create-quotation?step=${CreateQuotationStepsEnum.CUSTOMER_DETAILS}&quotationId=${data._id}&mode=edit`,
                );
              }}
            >
              <div className="text-black d-flex align-items-center gap-2 svg-20">
                Modify
              </div>
            </Menu.Item>
          )}

        {!archiveFilter && USER_PERMISSIONS.MODIFY && (
          <>
            {data?.status != QuoteStatus?.ADMIN_APPROVED ||
            [ROLE?.ADMIN, ROLE?.SUB_ADMIN]?.includes(user?.role?.key) ? (
              <Menu.Item
                key="duplicate"
                onClick={() => {
                  setIdToDuplicate(data._id);
                }}
              >
                <div className="text-black d-flex align-items-center gap-2 svg-20">
                  Duplicate
                </div>
              </Menu.Item>
            ) : (
              ""
            )}
            {!!data?.project_no && (
              <Menu.Item
                key="create-revision"
                onClick={() => {
                  setIdToCreateRevision(data._id);
                }}
              >
                <div className="text-black d-flex align-items-center gap-2 svg-20">
                  Create Revision
                </div>
              </Menu.Item>
            )}
          </>
        )}

        {!isCustomerLoggedIn() && USER_PERMISSIONS.READ && (
          <Menu.Item
            key="2"
            onClick={() => {
              if (scrollableDivRef.current) {
                sessionStorage.setItem(
                  QUOTATION_SCROLL_KEY,
                  String(scrollableDivRef.current.scrollTop),
                );
              }
              navigate(`/quotations/${data._id}/audit-log`, {
                state: {
                  backPath: "/quotations",
                },
              });
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Audit Log
            </div>
          </Menu.Item>
        )}

        {/* ✅ Archive / Unarchive Guard */}
        {[ROLE.ADMIN, ROLE.SUB_ADMIN].includes(user?.role?.key) &&
          USER_PERMISSIONS.MODIFY && (
            <Menu.Item
              key="toggle_archive"
              onClick={() => {
                setArchiveActionData({
                  id: data._id,
                  type: archiveFilter ? "unarchive" : "archive",
                });
              }}
            >
              <div className="text-black d-flex align-items-center gap-2 svg-20">
                {archiveFilter ? "Unarchive" : "Archive"}
              </div>
            </Menu.Item>
          )}

        {/* ✅ Remove Guard */}
        {USER_PERMISSIONS.REMOVE && (
          <Menu.Item key="3" onClick={() => setIdToDelete(data._id)}>
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Quote ID",
      dataIndex: "display_quote_id",
      key: "display_quote_id",
      sorter: true,
      width: 130,
      sortOrder: getAntdSortOrder("display_quote_id", sorterState),
      onCell: onCellClick,
      render: (display_quote_id: any, record: any) =>
        display_quote_id ? display_quote_id : "-",
    },
    {
      title: "Company Name",
      dataIndex: ["customer_id", "company_name"],
      key: "company_name",
      sorter: true,
      sortOrder: getAntdSortOrder("company_name", sorterState),
      onCell: onCellClick,
      width: 260,
      render: (val: any) => (
        <div
          style={{
            wordWrap: "break-word",
            wordBreak: "break-all",
            whiteSpace: "normal",
          }}
        >
          {val ? val : "-"}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: ["customer_snapshot", "email"],
      key: "email",
      sorter: true,
      width: 200,
      sortOrder: getAntdSortOrder("email", sorterState),
      onCell: onCellClick,
      render: (email: any) => {
        // 1. Decrypt the value first
        const decryptedEmail = email ? email : "-";

        // 2. Return the styled JSX with the decrypted value
        return (
          <div
            style={{
              wordWrap: "break-word",
              wordBreak: "break-all",
              whiteSpace: "normal",
              minWidth: "200px", // Helps ensure the column doesn't get too squashed
            }}
          >
            {decryptedEmail}
          </div>
        );
      },
      // render: (email: any) => (email ? decryptByText(email) : "-"),
    },
    {
      title: "Phone",
      dataIndex: ["customer_snapshot", "phone"],
      key: "phone",
      sorter: true,
      sortOrder: getAntdSortOrder("phone", sorterState),
      onCell: onCellClick,
      render: (phone: any) => (phone ? phone : "-"),
    },
    {
      title: "Amount",
      dataIndex: "grand_total",
      key: "grand_total",
      sorter: true,
      sortOrder: getAntdSortOrder("grand_total", sorterState),
      onCell: onCellClick,
      render: (amount: any, record: any) => {
        const rate = record.conversion_rate || 1;
        return (
          <span className="fw-bold">
            {formatCurrency(amount, record.currency, rate)}
          </span>
        );
      },
    },
    ...([ROLE.ADMIN, ROLE.SUB_ADMIN].includes(user?.role?.key)
      ? [
          {
            title: "Estimated Profit",
            dataIndex: "estimated_profit",
            key: "estimated_profit",
            sorter: true,
            sortOrder: getAntdSortOrder("estimated_profit", sorterState),
            render: (value: any, record: any) => {
              const profit_data = record?.profit_data;
              const profit = profit_data?.estimated_profit || 0;
              return (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfitData({
                      ...profit_data,
                      display_quote_id: record?.display_quote_id,
                      vat_percentages: record?.vat_percentage,
                      conversion_rate: record?.conversion_rate,
                      currency: record?.currency,
                    });
                    setProfitModal(true);
                  }}
                >
                  {profit > 0 ? (
                    <span className="text-success fw-semibold">
                      {formatCurrency(
                        Number(profit) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : profit < 0 ? (
                    <span className="text-danger fw-semibold">
                      {formatCurrency(
                        Number(Math.abs(profit)) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : (
                    formatCurrency(
                      Number(0) || 0,
                      record?.currency,
                      record?.conversion_rate,
                    )
                  )}
                </div>
              );
            },
          },
          {
            title: "Actual Profit",
            dataIndex: "total_profit",
            key: "total_profit",
            sorter: true,
            sortOrder: getAntdSortOrder("total_profit", sorterState),
            render: (value: any, record: any) => {
              const profit_data = record?.profit_data;
              const profit = profit_data?.total_profit || 0;
              return !profit_data?.is_order_created ? (
                <Tooltip
                  title={
                    "The actual profit calculation will be shown after the order is invoiced."
                  }
                >
                  <div className="d-flex">-</div>
                </Tooltip>
              ) : (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfitData({
                      ...profit_data,
                      display_quote_id: record?.display_quote_id,
                      vat_percentages: record?.vat_percentage,
                      conversion_rate: record?.conversion_rate,
                      currency: record?.currency,
                    });
                    setProfitModal(true);
                  }}
                >
                  {profit > 0 ? (
                    <span className="text-success fw-semibold">
                      {formatCurrency(
                        Number(profit) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : profit < 0 ? (
                    <span className="text-danger fw-semibold">
                      {formatCurrency(
                        Number(Math.abs(profit)) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : (
                    formatCurrency(
                      Number(0) || 0,
                      record?.currency,
                      record?.conversion_rate,
                    )
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: "Estimated Lead Time",
      dataIndex: "estimated_lead_time",
      key: "estimated_lead_time",
      width: 140,
      onCell: onCellClick,
      render: (_: any, record: any) => {
        const estimatedLeadTimeDays = record?.estimated_lead_time;
        if (estimatedLeadTimeDays == null) {
          return <span>-</span>;
        }
        const estimatedLeadTimeWeeks = Number(
          (estimatedLeadTimeDays / 7).toFixed(1),
        );
        return (
          <span className="fw-semibold text-primary">
            {estimatedLeadTimeWeeks.toFixed(1)}{" "}
            {estimatedLeadTimeWeeks === 1 ? "Week" : "Weeks"}
          </span>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      key: "created_by",
      sorter: true, // Assuming backend supports sorting by creator
      sortOrder: getAntdSortOrder("created_by", sorterState),
      onCell: onCellClick,
      hidden: archiveFilter,
      width: 150,
      render: (user: any, record: any) => {
        if (user && typeof user === "object") {
          const roleName = record?.created_by_role?.name || user?.role?.name;
          return (
            <span>
              {getDecryptedName(user)} {roleName ? `(${roleName})` : "-"}
            </span>
          );
        }
        return "-";
      },
    },

    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: getAntdSortOrder("created_at", sorterState),
      onCell: onCellClick,
      hidden: archiveFilter,
      render: (createdAt: any) =>
        createdAt ? formatToLocalDate(createdAt, true) : "-",
    },
    ...(archiveFilter
      ? [
          {
            title: "Archived by",
            dataIndex: "archived_by",
            key: "archived_by",
            sorter: true,
            sortOrder: getAntdSortOrder("archived_by", sorterState),
            onCell: onCellClick,
            render: (user: any) => {
              if (user && typeof user === "object") {
                return (
                  <span>
                    {getDecryptedName(user)}{" "}
                    {user?.role?.name ? `(${user?.role?.name})` : "-"}
                  </span>
                );
              }
              return "-";
            },
          },
          {
            title: "Archived on",
            dataIndex: "archived_at",
            key: "archived_at",
            sorter: true,
            sortOrder: getAntdSortOrder("archived_at", sorterState),
            onCell: onCellClick,
            render: (archivedAt: any) =>
              archivedAt ? formatToLocalDate(archivedAt, true) : "-",
          },
        ]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: getAntdSortOrder("status", sorterState),
      onCell: onCellClick,
      render: (status: any, record: any) => {
        const displayLabel = QuoteStatusLabels[status as QuoteStatus] || status;
        const isFinalInvoicePending = !!record?.enable_to_create_final_invoice;

        return (
          <div className="d-flex flex-column align-items-start gap-1">
            <div className="d-flex align-items-center gap-2">
              <span
                className={
                  "d-inline-flex align-items-center billStatus text-nowrap " +
                  statusFindClass(status)
                }
              >
                {svg.dotIcon}
                {displayLabel}
              </span>
              {isInvoiceFeatureEnabled &&
                INVOICE_WARNING_VISIBLE_ROLES.includes(user?.role?.key) &&
                record?.invoice_warnings &&
                record?.invoice_warnings.length > 0 && (
                  <Tooltip
                    title={
                      <div className="d-flex flex-column gap-1">
                        {record.invoice_warnings.map((w: string, i: number) => (
                          <span key={i}>- {w}</span>
                        ))}
                      </div>
                    }
                    placement="topRight"
                  >
                    <span className="text-warning svg-20 d-flex align-items-center cursor-pointer">
                      <ExclamationCircleFilled />
                    </span>
                  </Tooltip>
                )}
            </div>
            {isInvoiceFeatureEnabled &&
              isFinalInvoicePending &&
              record?.order_details?._id && (
                <span
                  onClick={async (e) => {
                    e.stopPropagation();
                    record?.order_details?._id &&
                      navigate(
                        `/invoices/create-invoice/${record?.order_details?._id}/final`,
                      );
                  }}
                  className="cursor-pointer fs-7 text-danger fw-normal"
                >
                  Final Invoice Pending -{" "}
                  <span style={{ textDecoration: "underline" }}>Create</span>
                </span>
              )}
          </div>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="d-flex" onClick={(e) => e.stopPropagation()}>
          {(USER_PERMISSIONS.READ ||
            USER_PERMISSIONS.MODIFY ||
            USER_PERMISSIONS.REMOVE) && (
            <Dropdown
              overlay={() => MoreOptionsMenu(record)}
              trigger={["click"]}
              placement="bottomRight"
              className="cursor-pointer"
              overlayClassName="moreOptionDropdown"
            >
              <span className="svg-32 svg-outline-primary" role="button">
                {svg?.tableMoreOption}
              </span>
            </Dropdown>
          )}
        </div>
      ),
    },
  ];

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (
    isArchiveView &&
    ![ROLE.ADMIN, ROLE.SUB_ADMIN].includes(user?.role?.key)
  ) {
    return <NoPermissionPage />;
  }

  return USER_PERMISSIONS.READ ? (
    <div className={`page-content h-100 px-40 py-40 ${isMobile ? "pb-0" : ""}`}>
      <div className="mb-md-3 mb-2 d-flex flex-column gap-md-2 gap-0">
        {/* Header */}
        <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
          <h2 className="text-black fw-bolder page-title mb-0 order-sm-1 order-1 d-flex align-items-center gap-1">
            {isArchiveView && (
              <span
                className="cursor-pointer d-flex align-items-center"
                onClick={() => navigate("/quotations")}
              >
                {svg?.backArrow}
              </span>
            )}
            {archiveFilter ? "Archived Quotations" : t("quotations.quotations")}{" "}
            <span>{isLoading ? "" : totalCount ? `(${totalCount})` : ""}</span>
          </h2>
          <div
            className={`pageSearch quotationsSearch mw-100 position-relative d-inline-block ms-auto order-sm-2 order-3`}
          >
            <input
              type="text"
              value={search}
              className="form-control form-control-lg"
              onChange={searchBarHandler}
              placeholder="Search"
            />
            {search?.length ? (
              <span className="cursor-pointer" onClick={searchBarClean}>
                {svg?.searchClear}
              </span>
            ) : (
              <span>{svg?.search}</span>
            )}
          </div>

          {!archiveFilter && USER_PERMISSIONS.ADD && (
            <div className="d-md-flex justify-content-end align-items-center order-sm-3 order-2 ms-sm-0 ms-auto">
              <button
                className="btn btn-lg btn-primary mob-btn"
                onClick={() => {
                  dispatch(resetQuotationState());
                  sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
                  sessionStorage.removeItem(QUOTATION_FILTER_KEY);
                  sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
                  if (isCustomerLoggedIn()) {
                    navigate(
                      `/quotations/create-quotation?step=${CreateQuotationStepsEnum.CUSTOMER_DETAILS}&customerId=${userId}`,
                    );
                  } else {
                    navigate(
                      `/quotations/create-quotation?step=${CreateQuotationStepsEnum.CUSTOMER_DETAILS}`,
                    );
                  }
                }}
              >
                + CREATE
              </button>
            </div>
          )}

          <div className="d-flex gap-md-3 gap-2 order-sm-3 order-2">
            <div
              onClick={toggleFilter}
              role="button"
              aria-expanded={isFilterOpen}
              className="filterButton position-relative ms-sm-0 ms-auto"
            >
              {svg?.filterIcon}
              {getFilterCount() > 0 && (
                <span className="position-absolute badge customBadgeFont rounded-pill bg-danger">
                  {getFilterCount()}
                </span>
              )}
            </div>
            <NotificationButton />
          </div>
        </div>

        {/* Filter Drawer */}
        <div
          className={`filter-transition-container overflow-hidden ${
            isFilterOpen ? "is-open" : ""
          }`}
        >
          <div className="d-flex flex-wrap align-items-end bg-white p-2 rounded-3 mt-2 position-relative">
            <label className="filterTitle fw-bold fs-5 position-absolute d-flex align-items-center">
              Filters
            </label>
            <div className="row gx-md-3 gx-2 gy-sm-3 gy-2 w-100">
              {/* Company Filter */}
              {/* Company Filter */}
              {!isCustomerLoggedIn() && (
                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Company
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      showSearch
                      allowClear
                      virtual={false}
                      value={companyFilter}
                      className="form-select form-select-lg"
                      placeholder="Select Company"
                      onChange={(val) => setCompanyFilter(val)}
                      onSearch={(val) => setCompanySearch(val)}
                      onClear={() => {
                        setCompanySearch("");
                        setCompanyFilter("");
                      }}
                      filterOption={false}
                      suffixIcon={companyFilter ? null : svg?.search}
                      optionLabelProp="label"
                      popupClassName="user-dropdown"
                      dropdownStyle={{ padding: "8px" }}
                    >
                      {/* ✅ "All" Option Logic */}
                      <Select.Option value="" label="All Companies">
                        <div className="py-2 fw-bold text-dark fs-6">
                          All Companies
                        </div>
                      </Select.Option>

                      {companyOptions
                        .filter((item) => {
                          const s = companySearch.toLowerCase();
                          return (
                            item.label.toLowerCase().includes(s) ||
                            item.companyName.toLowerCase().includes(s) ||
                            item.email.toLowerCase().includes(s)
                          );
                        })
                        .map((client) => {
                          const userLabel = (
                            <div
                              className="d-flex flex-column"
                              style={{ lineHeight: "1.2", padding: "2px 0" }}
                            >
                              <span className="fw-bold text-dark fs-6 gap-1 text-truncate w-100 pe-3">
                                <span>{client.label}</span>
                                <span>({client.companyName})</span>
                              </span>
                            </div>
                          );

                          return (
                            <Select.Option
                              key={client.value}
                              value={client.value}
                              label={userLabel}
                            >
                              <div className="d-flex flex-column py-1 border-bottom-0">
                                <span className="d-flex flex-wrap fw-bold text-dark fs-7 gap-0">
                                  <span>{client.label}</span>
                                  <span>({client.companyName})</span>
                                </span>
                                <span className="text-secondary fs-8">
                                  {client.email}
                                </span>
                              </div>
                            </Select.Option>
                          );
                        })}
                    </Select>
                  </div>
                </div>
              )}

              {/* Status Filter */}
              <div className="col-lg-auto col-md-4 col-6">
                <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                  Status
                </label>
                <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                  <Select
                    value={statusFilter}
                    className="form-select form-select-lg"
                    onChange={(value) => {
                      setStatusFilter(value);
                    }}
                    placeholder="All Statuses"
                    rootClassName="filter-status-dropdown"
                  >
                    <Select.Option value="">All</Select.Option>
                    {getQuoteStatusOptions().map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Created By Filter */}
              <div className="col-lg-auto col-md-4 col-6">
                <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                  Created By
                </label>
                <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                  <Select
                    showSearch
                    allowClear
                    virtual={false}
                    value={createdByFilter}
                    className="form-select form-select-lg"
                    placeholder="Select Created By"
                    onChange={(val) => setCreatedByFilter(val)}
                    onSearch={(val) => setCreatedBySearch(val)}
                    onClear={() => {
                      setCreatedBySearch("");
                      setCreatedByFilter("");
                    }}
                    filterOption={false}
                    suffixIcon={createdByFilter ? null : svg?.search}
                    optionLabelProp="label"
                    popupClassName="user-dropdown"
                    dropdownStyle={{ padding: "8px" }}
                  >
                    {/* ✅ "All" Option Logic */}
                    <Select.Option value="" label="All Users">
                      <div className="py-2 fw-bold text-dark fs-6">
                        All Users
                      </div>
                    </Select.Option>

                    {createdByOptions
                      .filter((item) => {
                        const s = createdBySearch.toLowerCase();
                        return (
                          item.label.toLowerCase().includes(s) ||
                          item.role.toLowerCase().includes(s)
                        );
                      })
                      .map((user) => {
                        const displayLabel = (
                          <div
                            className="d-flex flex-column"
                            style={{ lineHeight: "1.2", padding: "2px 0" }}
                          >
                            <span className="fw-bold text-dark fs-6 text-truncate w-100 pe-3">
                              {user.label}{" "}
                              <span className="fw-normal text-secondary">
                                ({user.role})
                              </span>
                            </span>
                          </div>
                        );

                        return (
                          <Select.Option
                            key={user.value}
                            value={user.value}
                            label={displayLabel}
                          >
                            <div className="d-flex flex-column py-1 border-bottom-0">
                              <span className="d-flex flex-wrap align-items-center fw-bold text-dark fs-7 gap-1">
                                {user.label}{" "}
                                <span className="text-secondary fs-8">
                                  ({user.role})
                                </span>
                              </span>
                            </div>
                          </Select.Option>
                        );
                      })}
                  </Select>
                </div>
              </div>

              {/* Archive Filter */}
              <div className="col-lg-auto col-md-4 col-sm-6">
                <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                  Archive
                </label>
                <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                  <Select
                    // style={{ width: "180px" }}
                    value={archiveFilter}
                    onChange={setArchiveFilter}
                    className="form-select form-select-lg"
                  >
                    <Select.Option value={false}>Unarchived</Select.Option>
                    <Select.Option value={true}>Archived</Select.Option>
                  </Select>
                </div>
              </div>

              {/* Date Filter */}
              <div className="col-lg-auto col-md-4 col-sm-6">
                <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                  Date Range
                </label>
                <div
                  className={`dateSelectFilter RangeDatePicker border-s mw-100 d-inline-block mb-sm-0 mb-2 ${
                    startDate === "" && endDate === ""
                      ? "not-active"
                      : "is-active"
                  } p-0`}
                >
                  <RangePicker
                    presets={rangePresets}
                    onChange={onRangeChange}
                    {...rangePickerProps}
                    value={
                      startDate && endDate
                        ? [dayjs(startDate), dayjs(endDate)]
                        : null
                    }
                    inputReadOnly={Capacitor.isNativePlatform()}
                  />
                </div>
              </div>

              {companyFilter ||
              startDate ||
              endDate ||
              createdByFilter ||
              statusFilter ||
              archiveFilter ||
              search ? (
                <ClearComponent
                  handleClick={() => {
                    setCompanySearch("");
                    setCompanyFilter("");
                    setStartDate("");
                    setEndDate("");
                    setCreatedByFilter("");
                    setCreatedBySearch("");
                    setStatusFilter("");
                    setArchiveFilter(false);
                    setSearch("");
                    setIsFilterOpen(false);
                  }}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        className={`card page-table-card page-table-device border-0 shadow rounded-4 flex-fill ${
          isMobile && isFilterOpen ? "" : "overflow-y-auto"
        }`}
      >
        <div
          className="card-body overflow-y-auto"
          id={scrollableDivId}
          ref={scrollableDivRef}
        >
          {isLoading || isMounting ? (
            <FullScreenLoader />
          ) : quotationsList?.length === 0 ? (
            <NoDataMessage
              icon={svg?.noQuotationIcon}
              text={
                archiveFilter
                  ? "You do not have any archived quotations yet."
                  : "You do not have any quotations yet."
              }
            />
          ) : (
            <InfiniteScroll
              dataLength={quotationsList.length}
              next={handleLoadMore}
              hasMore={!!hasNextPage}
              loader={
                isFetchingNextPage && (
                  <div className="text-center p-2">
                    <Spin tip={t("common.loading")} />
                  </div>
                )
              }
              scrollableTarget={scrollableDivId}
              height={"100%"}
              style={{ overflow: "auto", paddingBottom: isMobile ? 16 : 0 }}
            >
              {!isMobile ? (
                /* --- DESKTOP VIEW: Table element only --- */
                <Table
                  dataSource={quotationsList}
                  scroll={{ x: 1000 }}
                  pagination={false}
                  columns={columns}
                  onChange={handleTableChange}
                  rowKey={(record) => record._id}
                  className="custom-table quotationListTable"
                  bordered
                  rowClassName={(record) => {
                    const creatorRole =
                      record?.created_by_role?.key ||
                      record.created_by?.role?.key;
                    const isCreatedByClient = [
                      RoleTypesEnum.CLIENT,
                      RoleTypesEnum.DISTRIBUTOR,
                    ].includes(creatorRole);
                    return isCreatedByClient
                      ? "border-external-user"
                      : "border-sm-team";
                  }}
                />
              ) : (
                /* --- MOBILE VIEW: Card elements only --- */
                <div className="px-3">
                  <div className="row gx-3 gy-3">
                    {quotationsList.map((record) => (
                      <div className="col-md-6">
                        <div
                          key={record._id}
                          className="card mobile-table-card mobile-font-s rounded-4 p-3"
                          onClick={() => {
                            // Save scroll before navigating to details
                            if (scrollableDivRef.current) {
                              sessionStorage.setItem(
                                QUOTATION_SCROLL_KEY,
                                String(scrollableDivRef.current.scrollTop),
                              );
                            }
                            navigate(
                              `/quotations/${record._id}?tab=customer-details`,
                            );
                          }}
                          style={{
                            borderLeft: `5px solid ${
                              [
                                RoleTypesEnum.CLIENT,
                                RoleTypesEnum.DISTRIBUTOR,
                              ].includes(
                                record?.created_by_role?.key ||
                                  record.created_by?.role?.key,
                              )
                                ? "#ff8000" // external user color
                                : "#ffe100" // SM team color
                            }`,
                          }}
                        >
                          {/* Card Header */}
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-bold fs-5">
                              #{record.display_quote_id || "-"}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className={`billStatus ${statusFindClass(
                                  record.status,
                                )} d-inline-flex align-items-center`}
                              >
                                {svg.dotIcon}
                                {QuoteStatusLabels[
                                  record.status as QuoteStatus
                                ] || record.status}
                              </span>
                              <div onClick={(e) => e.stopPropagation()}>
                                <Dropdown
                                  overlay={() => MoreOptionsMenu(record)}
                                  trigger={["click"]}
                                  placement="bottomRight"
                                >
                                  <span className="cursor-pointer">
                                    {svg?.tableMobileOption}
                                  </span>
                                </Dropdown>
                              </div>
                            </div>
                          </div>

                          {/* Company Name */}
                          <h6 className="fw-bold fs-5 mb-2">
                            {record.customer_id?.company_name ||
                              record.customer_snapshot?.company_name ||
                              "-"}
                          </h6>

                          {/* Details Grid */}
                          <div className="mobile-card-details fs-6 fs-6-mob">
                            <div className="row gy-1">
                              {[
                                {
                                  label: "Email",
                                  value: record.customer_snapshot?.email
                                    ? record.customer_snapshot.email
                                    : "-",
                                },
                                {
                                  label: "Phone",
                                  value: record.customer_snapshot?.phone || "-",
                                },
                                {
                                  label: "Est. Lead Time",
                                  value: (() => {
                                    const estDays = record?.estimated_lead_time;
                                    if (estDays == null) {
                                      return <span>-</span>;
                                    }
                                    const estWeeks = Number(
                                      (estDays / 7).toFixed(1),
                                    );
                                    return (
                                      <span className="fw-semibold text-primary">
                                        {estWeeks.toFixed(1)}{" "}
                                        {estWeeks === 1 ? "Week" : "Weeks"}
                                      </span>
                                    );
                                  })(),
                                },
                                {
                                  label: "Created by",
                                  value: `${getDecryptedName(
                                    record.created_by,
                                  )}${
                                    record?.created_by_role?.name ||
                                    record?.created_by?.role?.name
                                      ? ` (${
                                          record?.created_by_role?.name ||
                                          record?.created_by?.role?.name
                                        })`
                                      : ""
                                  }`,
                                  hidden: archiveFilter,
                                },
                                {
                                  label: "Est.Profit",
                                  value: (
                                    <div
                                      className="cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setProfitData({
                                          ...record?.profit_data,
                                          display_quote_id:
                                            record?.display_quote_id,
                                          vat_percentages:
                                            record?.vat_percentage,
                                          conversion_rate:
                                            record?.conversion_rate,
                                          currency: record?.currency,
                                        });
                                        setProfitModal(true);
                                      }}
                                    >
                                      {record?.profit_data?.estimated_profit >
                                      0 ? (
                                        <span className="text-success fw-semibold">
                                          {formatCurrency(
                                            Number(
                                              record?.profit_data
                                                ?.estimated_profit,
                                            ) || 0,
                                            record?.currency,
                                            record?.conversion_rate,
                                          )}
                                        </span>
                                      ) : record?.profit_data
                                          ?.estimated_profit < 0 ? (
                                        <span className="text-danger fw-semibold">
                                          {formatCurrency(
                                            Number(
                                              Math.abs(
                                                record?.profit_data
                                                  ?.estimated_profit,
                                              ),
                                            ) || 0,
                                            record?.currency,
                                            record?.conversion_rate,
                                          )}
                                        </span>
                                      ) : (
                                        formatCurrency(
                                          Number(0) || 0,
                                          record?.currency,
                                          record?.conversion_rate,
                                        )
                                      )}
                                    </div>
                                  ),
                                  hidden: archiveFilter,
                                },
                                {
                                  label: "Created on",
                                  value: record.created_at
                                    ? formatToLocalDate(record.created_at, true)
                                    : "-",
                                  hidden: archiveFilter,
                                },
                                ...(archiveFilter
                                  ? [
                                      {
                                        label: "Archived by",
                                        value: record.archived_by
                                          ? `${getDecryptedName(
                                              record.archived_by,
                                            )}${
                                              record.archived_by?.role?.name
                                                ? ` (${record.archived_by.role.name})`
                                                : ""
                                            }`
                                          : "-",
                                      },
                                      {
                                        label: "Archived on",
                                        value: record.archived_at
                                          ? formatToLocalDate(
                                              record.archived_at,
                                              true,
                                            )
                                          : "-",
                                      },
                                    ]
                                  : []),
                              ].map((item, idx) => {
                                if (item?.hidden) return;
                                return (
                                  <div className="col-12" key={idx}>
                                    <div className="row">
                                      <div className="col-sm-3 col-4 text-secondary fw-medium">
                                        {item.label}:
                                      </div>
                                      <div className="col-sm-9 col-8">
                                        {item.value}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Footer / Amount */}
                          <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                            <span className="fw-bold">Amount:</span>
                            <span className="fw-bold fs-5">
                              {formatCurrency(
                                record.grand_total,
                                record.currency,
                                record.conversion_rate || 1,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      <CommonConfirmationModal
        open={idToDelete != null}
        title="Remove Quotation, Are You Sure?"
        subTitle="Removing this quotation will delete it permanently from the system. This action cannot be undone. Please confirm before proceeding."
        loading={deleting}
        onSuccess={handleDeleteConfirm}
        onClose={() => setIdToDelete(null)}
        confirmButtonText="REMOVE"
      />

      {/* Archive Modal */}
      <CommonConfirmationModal
        open={archiveActionData != null}
        title={
          archiveActionData?.type === "archive"
            ? "Archive Quotation, Are You Sure?"
            : "Unarchive Quotation, Are You Sure?"
        }
        subTitle={
          archiveActionData?.type === "archive"
            ? "Archiving this quotation will move it to the archived list. You can restore it later if needed."
            : "Unarchiving this quotation will restore it to the main quotations list."
        }
        loading={isTogglingArchive}
        onSuccess={handleArchiveConfirm}
        onClose={() => setArchiveActionData(null)}
        confirmButtonText={
          archiveActionData?.type === "archive" ? "ARCHIVE" : "UNARCHIVE"
        }
      />

      {idToOverride !== null && (
        <AddApprovalOverrideNoteModal
          open={idToOverride != null}
          onClose={() => setIdToOverride(null)}
          quote_id={idToOverride}
          refreshData={(data) => {
            queryClient.setQueryData(
              ["quotations", queryFilters],
              (old: any) => {
                if (!old) return old;
                return {
                  ...old,
                  pages: old.pages.map((page: any) => ({
                    ...page,
                    quotes: (page.quotes || []).map((quote: any) =>
                      quote._id === data._id ? { ...quote, ...data } : quote,
                    ),
                  })),
                };
              },
            );
          }}
        />
      )}

      {profitModal && (
        <ViewProfitDataModal
          visible={!!profitModal}
          setIsModalVisible={setProfitModal as any}
          onClose={() => {
            setProfitModal(false);
            setProfitData(null);
          }}
          profitData={profitData}
        />
      )}

      <DownloadMailerAppModal
        open={showDesktopAppError}
        onClose={() => setShowDesktopAppError(false)}
      />

      <CommonConfirmationModal
        open={pendingSentQuoteId != null}
        onClose={() => {
          if (mailerTimeoutRef.current) clearTimeout(mailerTimeoutRef.current);
          setPendingSentQuoteId(null);
        }}
        onSuccess={() => {
          if (mailerTimeoutRef.current) clearTimeout(mailerTimeoutRef.current);
          if (pendingSentQuoteId) {
            const targetId = pendingSentQuoteId;
            setPendingSentQuoteId(null);
            executeStatusUpdate("sent", targetId, pendingSentForceUpdate);
          }
        }}
        title="Has the Email Been Sent?"
        subTitle="Please confirm once you have sent the email to the client using your default mail app."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Yes, Email Sent"
        isContantLoading={isContantLoading}
      />

      <CommonConfirmationModal
        open={idToNotify != null}
        onClose={() => setIdToNotify(null)}
        onSuccess={() => {
          setIsNotifying(true);
          const payload = {
            quote_id: idToNotify,
          };
          dispatch(
            notifyClientAPI({
              payload,
              onSuccess: () => {
                setIsNotifying(false);
                setIdToNotify(null);
              },
              onWarnings: (warnings, severity) => {
                setIsNotifying(false);
                setApiWarnings(warnings);
                setApiWarningSeverity(severity || "");
                setPendingNotifyId(idToNotify);
                setIdToNotify(null);
              },
              onError: () => {
                setIsNotifying(false);
              },
            }),
          );
        }}
        title="Notify to Client, Are You Sure?"
        subTitle="This action will send an email to the client with the relevant details. Please confirm before proceeding."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Proceed"
        loading={isNotifying}
      />

      <CommonConfirmationModal
        open={idToReopen != null}
        onClose={() => setIdToReopen(null)}
        onSuccess={() => {
          setIsReopening(true);
          const payload = {
            quote_id: idToReopen,
          };
          dispatch(
            reopenQuotationAPI({
              payload,
              onSuccess: (data) => {
                queryClient.setQueryData(
                  ["quotations", queryFilters],
                  (old: any) => {
                    if (!old) return old;
                    return {
                      ...old,
                      pages: old.pages.map((page: any) => ({
                        ...page,
                        quotes: (page.quotes || []).map((quote: any) =>
                          quote._id === data._id
                            ? { ...quote, status: data.status }
                            : quote,
                        ),
                      })),
                    };
                  },
                );
                setIsReopening(false);
                setIdToReopen(null);
              },
              onError: () => {
                setIsReopening(false);
              },
            }),
          );
        }}
        title="Re-Open Quotation, Are You Sure?"
        subTitle={`Reopening this quotation will change its status to "Draft." Any previously completed approvals may need to be completed again. Are you sure you want to continue?`}
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Proceed"
        loading={isReopening}
      />

      {isCreateOrder && (
        <CreateOrderConfirmationModal
          open={isCreateOrder}
          onClose={() => {
            setIsCreateOrder(false);
            pendingOrderPayloadRef.current = null;
          }}
          onSuccess={() => {
            setSubmittingCreateOrder(true);
            const initialPayload = { quote_id: selectedQuoteData?._id };
            pendingOrderPayloadRef.current = initialPayload;
            dispatch(
              createOrderAPI({
                payload: initialPayload,
                onSuccess: (resData) => {
                  setSubmittingCreateOrder(false);
                  pendingOrderPayloadRef.current = null;
                  navigate(`/orders/${resData?._id}`);
                  setIsCreateOrder(false);
                },
                onError: () => {
                  setSubmittingCreateOrder(false);
                },
                onAddressIncompleteError: (errData) => {
                  setIsCreateOrder(false);
                  setFailedAddressData(errData?.data);
                },
                onDrawingRequiredError: (data) => {
                  setIsCreateOrder(false);
                  setDrawingRequiredData(data?.data || {});
                },
                onCustomerOnHoldError: (data: any) => {
                  setIsCreateOrder(false);
                  setCustomerOnHoldModal(true);
                  setCustomerOnHoldMessage(data);
                },
              }),
            );
          }}
          quoteDisplayId={selectedQuoteData?.display_quote_id as string}
          submitting={submittingCreateOrder}
        />
      )}
      {customerOnHoldModal && (
        <CustomerOnHoldWarningModal
          visible={customerOnHoldModal}
          onClose={() => setCustomerOnHoldModal(false)}
          customerOnHoldMessage={customerOnHoldMessage}
        />
      )}

      <CommonConfirmationModal
        open={idToDuplicate != null}
        onClose={() => setIdToDuplicate(null)}
        onSuccess={() => {
          setIsDuplicating(true);
          const payload = {
            quote_id: idToDuplicate,
          };
          dispatch(
            duplicateQuotationAPI({
              payload,
              onSuccess: () => {
                sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
                sessionStorage.removeItem(QUOTATION_FILTER_KEY);
                sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
                setIsDuplicating(false);
                setIdToDuplicate(null);
                queryClient.removeQueries({ queryKey: ["quotations"] });
              },
              onError: () => {
                setIsDuplicating(false);
              },
              onSyncReviewRequired: (syncData: any) => {
                setIsDuplicating(false);
                const currentId = idToDuplicate;
                setIdToDuplicate(null);
                setSyncReviewModalData({
                  actionType: "duplicate",
                  quoteId: currentId as string,
                  warnings: syncData?.warnings || [],
                  updates: syncData?.updates || [],
                });
              },
            }),
          );
        }}
        title="Duplicate Quotation, Are You Sure?"
        subTitle="This action will create a copy of the selected quotation with the same details in the Draft stage. You can modify the duplicated quotation as needed."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Duplicate"
        loading={isDuplicating}
      />

      <CommonConfirmationModal
        open={!!idToCreateRevision}
        onClose={() => setIdToCreateRevision(null)}
        onSuccess={() => {
          setIsCreatingRevision(true);
          const payload = {
            quote_id: idToCreateRevision,
          };
          dispatch(
            createRevisionQuotationAPI({
              payload,
              onSuccess: () => {
                sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
                sessionStorage.removeItem(QUOTATION_FILTER_KEY);
                sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
                setIsCreatingRevision(false);
                setIdToCreateRevision(null);
                queryClient.removeQueries({ queryKey: ["quotations"] });
              },
              onError: () => {
                setIsCreatingRevision(false);
              },
              onSyncReviewRequired: (syncData: any) => {
                setIsCreatingRevision(false);
                const currentId = idToCreateRevision;
                setIdToCreateRevision(null);
                setSyncReviewModalData({
                  actionType: "revision",
                  quoteId: currentId as string,
                  warnings: syncData?.warnings || [],
                  updates: syncData?.updates || [],
                });
              },
            }),
          );
        }}
        title="Create Revision, Are You Sure?"
        subTitle="Creating a revision will generate a new version of this record. The current version will be preserved. Do you want to continue?"
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Create Revision"
        loading={isCreatingRevision}
      />

      {syncReviewModalData && (
        <QuotationSyncReviewModal
          open={!!syncReviewModalData}
          actionType={syncReviewModalData.actionType}
          warnings={syncReviewModalData.warnings}
          updates={syncReviewModalData.updates}
          loading={isConfirmingSync}
          onClose={() => {
            if (!isConfirmingSync) {
              setSyncReviewModalData(null);
            }
          }}
          onConfirm={() => {
            setIsConfirmingSync(true);
            const payload = {
              quote_id: syncReviewModalData.quoteId,
              confirm_changes: true,
            };

            const handleSuccess = () => {
              sessionStorage.removeItem(QUOTATION_SCROLL_KEY);
              sessionStorage.removeItem(QUOTATION_FILTER_KEY);
              sessionStorage.setItem(QUOTATION_REFETCH_KEY, "1");
              setIsConfirmingSync(false);
              setSyncReviewModalData(null);
              queryClient.removeQueries({ queryKey: ["quotations"] });
            };

            const handleError = () => {
              setIsConfirmingSync(false);
            };

            if (syncReviewModalData.actionType === "duplicate") {
              dispatch(
                duplicateQuotationAPI({
                  payload,
                  onSuccess: handleSuccess,
                  onError: handleError,
                }),
              );
            } else {
              dispatch(
                createRevisionQuotationAPI({
                  payload,
                  onSuccess: handleSuccess,
                  onError: handleError,
                }),
              );
            }
          }}
        />
      )}

      <CommonConfirmationModal
        open={idToAdminApprove != null}
        onClose={() => {
          if (!isAdminApproving) {
            setIdToAdminApprove(null);
          }
        }}
        onSuccess={() => {
          if (idToAdminApprove) {
            setIsAdminApproving(true);
            executeStatusUpdate("admin_approved", idToAdminApprove, false, {
              onSuccess: () => {
                setIdToAdminApprove(null);
              },
              onFinally: () => {
                setIsAdminApproving(false);
              },
            });
          }
        }}
        title="Mark This Quotation as Admin Approved?"
        subTitle="Are you sure you want to mark this quotation as Admin Approved? This will update the quotation's approval status and allow it to proceed to the next stage of the workflow."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Approve"
        loading={isAdminApproving}
      />

      <CommonConfirmationModal
        open={idToDownloadAndMarkSent != null}
        onClose={() => {
          if (!isDownloadingAndMarkingSent) {
            setIdToDownloadAndMarkSent(null);
          }
        }}
        onSuccess={() => {
          if (idToDownloadAndMarkSent) {
            const targetQuoteId = idToDownloadAndMarkSent;
            setIsDownloadingAndMarkingSent(true);
            const closeLoadingMsg = message.loading(
              "Generating PDF and updating status...",
              0,
            );

            // Step 1: Generate & download PDF
            dispatch(
              generateQuotationPdfAPI({
                payload: { quote_id: targetQuoteId },
                onSuccess: () => {
                  // Step 2: On PDF success, update status to 'sent'
                  executeStatusUpdate("sent", targetQuoteId, false, {
                    onSuccess: () => {
                      closeLoadingMsg();
                      setIsDownloadingAndMarkingSent(false);
                      setIdToDownloadAndMarkSent(null);
                    },
                    onError: () => {
                      closeLoadingMsg();
                      setIsDownloadingAndMarkingSent(false);
                      message.warning(
                        "Quotation PDF downloaded, but failed to update status to Sent. Please try again.",
                      );
                    },
                  });
                },
                onError: () => {
                  closeLoadingMsg();
                  setIsDownloadingAndMarkingSent(false);
                  message.error(
                    "Failed to generate Quotation PDF. Status was not changed.",
                  );
                },
              }),
            );
          }
        }}
        title="Download & Mark as Sent?"
        subTitle="Are you sure you want to download this Quotation PDF & change status as Sent?"
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Proceed"
        loading={isDownloadingAndMarkingSent}
      />

      <CommonConfirmationModal
        open={pendingApprovalCustomerId != null}
        onClose={() => setPendingApprovalCustomerId(null)}
        onSuccess={() => {
          const custId = pendingApprovalCustomerId;
          setPendingApprovalCustomerId(null);
          if (custId) {
            navigate(`/clients-distributors/${custId}`);
          }
        }}
        title="Customer Approval Required"
        subTitle="This customer has not been approved yet. Please review and approve the customer details before approving this quotation."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="View Customer Details"
      />

      <WarningsModal
        warnings={apiWarnings}
        onClose={() => {
          setApiWarnings([]);
          setApiWarningSeverity("");
          setPendingNotifyId(null);
          setPendingStatusData(null);
        }}
        actions={
          apiWarningSeverity === WarningSeverity.BLOCKING
            ? WarningActionLayouts.Blocking(() => {
                setApiWarnings([]);
                setApiWarningSeverity("");
                setPendingNotifyId(null);
                setPendingStatusData(null);
              })
            : WarningActionLayouts.BypassableSave(
                () => {
                  setApiWarnings([]);
                  setApiWarningSeverity("");
                  setPendingNotifyId(null);
                  setPendingStatusData(null);
                },
                () => {
                  if (pendingNotifyId) {
                    setIsNotifying(true);
                    const payload = {
                      quote_id: pendingNotifyId,
                      force_update: true,
                    };
                    dispatch(
                      notifyClientAPI({
                        payload,
                        onSuccess: () => {
                          setIsNotifying(false);
                          setPendingNotifyId(null);
                          setApiWarnings([]);
                          setApiWarningSeverity("");
                        },
                        onWarnings: (warnings, severity) => {
                          setIsNotifying(false);
                          setApiWarnings(warnings);
                          setApiWarningSeverity(severity || "");
                        },
                        onError: () => {
                          setIsNotifying(false);
                        },
                      }),
                    );
                  } else if (pendingStatusData) {
                    changeStatusHandler(
                      pendingStatusData.status,
                      pendingStatusData.quotationId,
                      true,
                    );
                  }
                },
                isNotifying || isChangingStatus,
              )
        }
      />

      {drawingRequiredData !== null && (
        <ConfigureDrawingsModal
          open={drawingRequiredData !== null}
          onClose={() => {
            setDrawingRequiredData(null);
            pendingOrderPayloadRef.current = null;
          }}
          drawingsData={drawingRequiredData?.variants_requiring_drawing}
          partsData={drawingRequiredData?.parts_requiring_drawing}
          componentsData={drawingRequiredData?.components_requiring_drawing}
          fullDrawingData={drawingRequiredData}
          quotationId={drawingRequiredData?.quote_id}
          onSubmit={(payload, setLoader) => {
            setLoader(true);
            const combinedPayload = {
              ...(pendingOrderPayloadRef.current || {}),
              ...payload,
            };
            pendingOrderPayloadRef.current = combinedPayload;
            dispatch(
              createOrderAPI({
                payload: combinedPayload,
                onSuccess: (data) => {
                  setLoader(false);
                  pendingOrderPayloadRef.current = null;
                  navigate(`/orders/${data?._id}`);
                  setIsCreateOrder(false);
                },
                onError: () => {
                  setLoader(false);
                },
                onAddressIncompleteError: (data) => {
                  setLoader(false);
                  setDrawingRequiredData(null);
                  setIsCreateOrder(false);
                  setFailedAddressData(data?.data);
                },
                onDrawingRequiredError: (data) => {
                  setIsCreateOrder(false);
                  setDrawingRequiredData(data?.data || {});
                },
              }),
            );
          }}
        />
      )}

      {failedAddressData !== null && (
        <ModifyDeliveryAddressModal
          open={failedAddressData !== null}
          onClose={() => {
            setFailedAddressData(null);
            pendingOrderPayloadRef.current = null;
          }}
          addressData={failedAddressData?.customer_snapshot?.delivery_address}
          isDistributor={false}
          onSubmitOverride={(payload, setSaving) => {
            setSaving(true);
            const combinedPayload = {
              ...(pendingOrderPayloadRef.current || {}),
              ...payload,
            };
            pendingOrderPayloadRef.current = combinedPayload;
            dispatch(
              createOrderAPI({
                payload: combinedPayload,
                onSuccess: (data) => {
                  setSaving(false);
                  setFailedAddressData(null);
                  pendingOrderPayloadRef.current = null;
                  navigate(`/orders/${data?._id}`);
                },
                onError: () => {
                  setSaving(false);
                },
                onAddressIncompleteError: (data) => {
                  setFailedAddressData(data?.data);
                },
                onDrawingRequiredError: (data) => {
                  setFailedAddressData(null);
                  setDrawingRequiredData(data?.data || {});
                },
              }),
            );
          }}
          quoteData={failedAddressData}
        />
      )}
      <DesktopUpdateModal
        visible={showDesktopUpdateModal}
        onClose={() => {
          setShowDesktopUpdateModal(false);
          pendingSendMailRef.current = null;
        }}
        onProceed={() => {
          if (pendingSendMailRef.current) {
            pendingSendMailRef.current();
            pendingSendMailRef.current = null;
          }
        }}
        requiredVersion={process.env.REACT_APP_DESKTOP_APP_VERSION || "1.0.1"}
        currentVersion={desktopAppCurrentVersion}
      />
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default Quotations;
